/**
 * Address paste parsing — CONTRACT-RECIPIENTS §2.
 *
 * A handover arrives as twelve addresses in a mail, and twelve trips through the Add
 * recipient dialog is twelve chances to paste one lender's desk onto another lender's
 * client. So the dialog takes the whole paste at once — and the moment it does, the
 * console has to answer three questions before anything is written: which addresses did
 * I find, which are already configured, and which are not addresses at all.
 *
 * Two implementations of this parse exist (this one for the readback, the API's for the
 * write), so the algorithm is pinned by the contract rather than described, and both
 * sides are tested against the SAME fixture — `tests/fixtures/address_paste.json`. A
 * test that hardcoded both sides would pass happily while the two parsers drifted.
 *
 * The order of the steps is load-bearing:
 *
 *   1. U+00A0 and U+200B become spaces. Both arrive from Outlook and Excel pastes and
 *      neither is a separator anywhere else, so a non-breaking space between two
 *      addresses would otherwise weld them into one unparseable token.
 *   2. `Display Name <addr>` spans come out FIRST, because a display name contains the
 *      very spaces step 3 splits on.
 *   3. What is left splits on commas, semicolons and whitespace.
 *
 * What this module does NOT do is decide anything. It reports; the server validates and
 * writes. The readback exists so the operator sees the plan before they commit to it.
 */

/**
 * Transcribed from `services/api/validate.py::_EMAIL_RE`, which is the authority — that
 * function validates every single create today, and the bulk route reuses it rather than
 * growing a second regex. `web/test/addresses.test.mjs` reads the Python source and fails
 * if these two drift, because a console that calls an address valid and a server that
 * refuses it produces "nothing was written" with no visible cause.
 *
 * Deliberately permissive, for the reason the Python comment gives: an address SES accepts
 * and a regex does not is a support ticket, and the real validation is the bounce.
 */
export const EMAIL_RE = /^[^@\s,;<>]{1,64}@[A-Za-z0-9][A-Za-z0-9.-]{0,252}\.[A-Za-z]{2,63}$/;

/** `validate_email` refuses anything longer, before the regex runs. */
export const MAX_EMAIL_LENGTH = 254;

/** Same limit a single create applies to `name`. */
export const MAX_NAME_LENGTH = 128;

/**
 * §2 limits. `nrd-recipients` holds tens of rows per client; a paste larger than this is a
 * mistake, and a mistake that writes 4,000 rows is not one anybody unpicks by hand. The
 * server refuses with `bulk_too_large`; the console says so before the round trip.
 */
export const MAX_BULK_ADDRESSES = 100;
export const MAX_BULK_INPUT_CHARS = 20_000;

/** The pinned display-name pattern. Group 1 is the name, group 2 the address. */
const NAMED_SPAN = /("?[^"<>,;\n]*"?)\s*<([^<>\s]+)>/g;

/** Everything that is not a separator, once the named spans have been lifted out. */
const BARE_TOKEN = /[^,;\s]+/g;

export function isValidEmail(value: string): boolean {
  const text = value.trim();
  return text.length > 0 && text.length <= MAX_EMAIL_LENGTH && EMAIL_RE.test(text);
}

/** The domain after the LAST `@`, case-folded. Empty when there is no domain to name. */
export function domainOf(email: string): string {
  const at = email.lastIndexOf('@');
  return at === -1 ? '' : email.slice(at + 1).trim().toLowerCase();
}

export interface ParsedAddress {
  /** 0-based position in the paste. Mirrors `errors.invalid[].index` in the API's refusal. */
  index: number;
  /** The text as it was pasted — a bad entry is named by what the operator typed. */
  input: string;
  name: string;
  email: string;
  valid: boolean;
  /** Why it is not an address. Present only when `valid` is false. */
  reason?: string;
}

/**
 * Parse a paste into addresses, in order of first appearance.
 *
 * A list of strings is joined and run through the same parser, so a paste and a
 * pre-split list cannot behave differently — which is the whole reason the API accepts
 * both shapes on one field.
 */
export function parseAddresses(input: string | string[]): ParsedAddress[] {
  const raw = Array.isArray(input) ? input.join('\n') : input;
  const text = raw.replace(/[\u00A0\u200B]/g, ' ');

  interface Found {
    at: number;
    input: string;
    name: string;
    email: string;
  }
  const found: Found[] = [];

  // Lift out `Name <addr>` first, blanking each span in place so the positions of
  // everything else — and therefore the order — survive step 3.
  //
  // split('') and NOT Array.from(): `match.index` is a UTF-16 offset, while Array.from
  // splits into code points. One astral character anywhere before a named span — an emoji
  // in a pasted display name is enough — shifted every index after it, so the blanking
  // landed on the wrong characters and the parse silently corrupted.
  const masked = text.split('');
  const named = new RegExp(NAMED_SPAN.source, 'g');
  let match: RegExpExecArray | null;
  while ((match = named.exec(text)) !== null) {
    // The name group is greedy across spaces, so `ops@a.test Head <head@b.test>` captured
    // `ops@a.test Head` and the first address vanished entirely — not created, not flagged
    // invalid, not flagged duplicate. A display name cannot contain an address, so every
    // @-bearing token inside the capture is left unmasked for the bare pass, and the name
    // begins after the last of them. Mirrors services/api/validate.py::parse_address_list.
    const rawName = match[1]!;
    let nameFrom = 0;
    for (const token of rawName.matchAll(/\S+/g)) {
      if (token[0]!.includes('@')) nameFrom = token.index! + token[0]!.length;
    }
    const start = match.index + nameFrom;
    const name = rawName.slice(nameFrom).replace(/"/g, '').trim().slice(0, MAX_NAME_LENGTH);
    found.push({
      at: start,
      input: text.slice(start, match.index + match[0]!.length).trim(),
      name,
      email: match[2]!.trim(),
    });
    for (let i = start; i < match.index + match[0]!.length; i += 1) masked[i] = ' ';
    // A zero-length match cannot happen (the pattern requires `<…>`), but an empty
    // advance here would spin forever if it ever did.
    if (match[0]!.length === 0) named.lastIndex += 1;
  }

  const rest = masked.join('');
  const bare = new RegExp(BARE_TOKEN.source, 'g');
  while ((match = bare.exec(rest)) !== null) {
    found.push({ at: match.index, input: match[0]!, name: '', email: match[0]! });
  }

  found.sort((a, b) => a.at - b.at);

  return found.map((entry, index) => {
    const valid = isValidEmail(entry.email);
    return {
      index,
      input: entry.input,
      name: entry.name,
      email: entry.email.trim(),
      valid,
      // The same sentence the API's `errors.invalid[].reason` carries, so the two
      // readbacks do not describe the same rejection in two different ways.
      ...(valid ? {} : { reason: 'not a valid email address' }),
    };
  });
}

export type DuplicateReason = 'already_configured' | 'repeated_in_input';

export interface PlannedDuplicate extends ParsedAddress {
  reason: DuplicateReason;
  /** The row it collides with, when that row already exists. Null for a repeat in the paste. */
  recipient_id: string | null;
}

export interface AddressPlan {
  /** Everything parsed, in order, valid or not. */
  entries: ParsedAddress[];
  /** What a write would create. */
  create: ParsedAddress[];
  /** Already configured, or repeated within this paste. Reported, never refused. */
  duplicates: PlannedDuplicate[];
  /** Malformed entries. Any of these and NOTHING is written. */
  invalid: ParsedAddress[];
  /** Every distinct domain in the paste, first appearance first. Named in the right-party tick. */
  domains: string[];
  /** True when the paste exceeds the §2 limits; the server refuses it with `bulk_too_large`. */
  tooLarge: boolean;
}

export interface ExistingAddress {
  recipient_id: string;
  email: string;
}

/**
 * What a write would do, given what this client already has.
 *
 * Duplicate matching is case-folded — `Ops@Fusion.com` and `ops@fusion.com` are one
 * recipient — against the client's rows AND against earlier entries in the same paste.
 * Addresses are reported exactly as typed; only the comparison folds, because the stored
 * spelling is the one that appears on the message.
 *
 * A duplicate is not an error. The operator's intent (this desk should receive this
 * client's reports) is already satisfied, so it must not fail the request and must not be
 * written twice.
 */
export function planAddresses(
  input: string | string[],
  existing: ReadonlyArray<ExistingAddress> = [],
): AddressPlan {
  const entries = parseAddresses(input);
  const known = new Map<string, string>();
  for (const row of existing) known.set(row.email.trim().toLowerCase(), row.recipient_id);

  const seen = new Set<string>();
  const create: ParsedAddress[] = [];
  const duplicates: PlannedDuplicate[] = [];
  const invalid: ParsedAddress[] = [];
  const domains: string[] = [];

  for (const entry of entries) {
    if (!entry.valid) {
      invalid.push(entry);
      continue;
    }
    const folded = entry.email.toLowerCase();
    const domain = domainOf(entry.email);
    if (domain && !domains.includes(domain)) domains.push(domain);

    const configured = known.get(folded);
    if (configured !== undefined) {
      duplicates.push({ ...entry, reason: 'already_configured', recipient_id: configured });
      continue;
    }
    if (seen.has(folded)) {
      duplicates.push({ ...entry, reason: 'repeated_in_input', recipient_id: null });
      continue;
    }
    seen.add(folded);
    create.push(entry);
  }

  const raw = Array.isArray(input) ? input.join('\n') : input;
  return {
    entries,
    create,
    duplicates,
    invalid,
    domains,
    tooLarge: entries.length > MAX_BULK_ADDRESSES || raw.length > MAX_BULK_INPUT_CHARS,
  };
}

/**
 * The domain set as one comparable string, for invalidating a right-party confirmation.
 *
 * The single-address dialog already re-asks when the address is edited, for the reason
 * its own comment gives: a tick made for one desk must not carry over to another. A paste
 * needs the same rule one level up — the tick names the domains, so it dies the moment
 * the set of domains changes.
 */
export function domainKey(domains: ReadonlyArray<string>): string {
  return [...domains].sort().join(',');
}

/** "fusion.com and hdbfs.com", "a, b and c" — for a sentence, not a list. */
export function joinDomains(domains: ReadonlyArray<string>): string {
  if (domains.length === 0) return '';
  if (domains.length === 1) return domains[0]!;
  return `${domains.slice(0, -1).join(', ')} and ${domains[domains.length - 1]!}`;
}
